import moment from 'moment';
import { Translate } from 'react-admin';
import { ExpiryTimeGranularityType, ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';

export type DisplayTimeGranularity = ExpiryTimeGranularity & { approximate: boolean };

// Month starts from 28 days when rounded to the nearest time granularity as the number of days in a month can vary
// The exact values are used for calculating the milliseconds to the nearest time granularity, so the months are always 30 days
const expiryTimeGranularityMap = (rounded = true) => {

  return {
    day: 1000 * 60 * 60 * 24,
    week: 1000 * 60 * 60 * 24 * 7,
    month: rounded ? 1000 * 60 * 60 * 24 * 28 : 1000 * 60 * 60 * 24 * 30,
    year: 1000 * 60 * 60 * 24 * 365,
  };

};

const DAYS_IN_PERIOD = {
  [ExpiryTimeGranularityType.Day]: 1,
  [ExpiryTimeGranularityType.Week]: 7,
  [ExpiryTimeGranularityType.Month]: 28,
  [ExpiryTimeGranularityType.Year]: 365,
};

const ROUND_UP_THRESHOLDS = {
  [ExpiryTimeGranularityType.Day]: 7,
  [ExpiryTimeGranularityType.Week]: 4,
  [ExpiryTimeGranularityType.Month]: 10,
};

const getDisplayPeriod = (days: number): ExpiryTimeGranularityType => {

  const weeks = days / DAYS_IN_PERIOD[ExpiryTimeGranularityType.Week];
  const months = days / DAYS_IN_PERIOD[ExpiryTimeGranularityType.Month];

  if (months >= ROUND_UP_THRESHOLDS[ExpiryTimeGranularityType.Month]) return ExpiryTimeGranularityType.Year;
  if (weeks >= ROUND_UP_THRESHOLDS[ExpiryTimeGranularityType.Week]) return ExpiryTimeGranularityType.Month;
  if (days >= ROUND_UP_THRESHOLDS[ExpiryTimeGranularityType.Day]) return ExpiryTimeGranularityType.Week;
  return ExpiryTimeGranularityType.Day;

};

const roundToPeriod = (days: number, period: ExpiryTimeGranularityType): DisplayTimeGranularity => {

  const periodDays = DAYS_IN_PERIOD[period];
  const value = Math.max(1, Math.round(days / periodDays));
  const approximate = Math.abs(days - value * periodDays) >= 1;

  return {
    type: period,
    value,
    approximate,
  };

};

export const getNearestExpiryTimeGranularity = (milliseconds: number): DisplayTimeGranularity => {

  const days = Math.abs(milliseconds) / expiryTimeGranularityMap().day;
  return roundToPeriod(days, getDisplayPeriod(days));

};

export const getExpiryTimeGranularity = (
  startDate: string,
  expiryDate: string,
): ExpiryTimeGranularity => {

  const ms = moment(expiryDate).startOf('day')
    .diff(moment(startDate).startOf('day'));
  return getNearestExpiryTimeGranularity(ms);

};

export const roundDaysToExpiryTimeGranularity = (
  days: number,
): DisplayTimeGranularity => {

  const dayMs = expiryTimeGranularityMap().day;
  return getNearestExpiryTimeGranularity(days * dayMs);

};

export const TimeGranularityLabel = (
  timeGranularity: DisplayTimeGranularity | ExpiryTimeGranularity | undefined,
  translate: Translate,
) => {

  if (!timeGranularity) {

    return translate('general.text.none');

  }

  const label = translate(
    `general.text.time_granularity_types.${timeGranularity.type}`,
    { smart_count: timeGranularity.value },
  );

  return 'approximate' in timeGranularity && timeGranularity.approximate
    ? `≈ ${label}`
    : label;

};

export const convertTimeGranularityToDays = (timeGranularity: ExpiryTimeGranularity): number => {

  return (timeGranularity.value * expiryTimeGranularityMap(false)[timeGranularity.type]) / expiryTimeGranularityMap(false)[ExpiryTimeGranularityType.Day];

};

export const calculateLargestReminderPeriodInDays = (
  reminderPeriods: ExpiryTimeGranularity[],
): number => Math.max(0, ...(reminderPeriods ?? []).map(convertTimeGranularityToDays));

export const getMinimumNextDate = (startDate?: string): string => moment(startDate ?? undefined).startOf('day').add(1, 'day').format('YYYY-MM-DD');

export const calculateDateAfterTimeGranularity = (startDate: string | undefined, timeGranularity: ExpiryTimeGranularity | undefined) => {

  if (!startDate || !timeGranularity || !timeGranularity.type || timeGranularity.value === null) {

    return undefined;

  }

  return moment(startDate).add(timeGranularity.value, timeGranularity.type).format('YYYY-MM-DD');

};
